import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useAppSelector,
  useQuery,
  useAppDispatch,
  useGetProductVersionsQuery,
  getLocalRetentionUser,
  parseRedirectAccountFromHkey,
  updateLocalRedirectObj,
  useUserLoginQuery,
  useGetUserOrgNodesQuery,
  useGetRegionsQuery,
  copyObj,
  Apploader,
  getSafeUrl,
  useLazyRegionLoginQuery,
  useLazyGetHaInstanceAccessUrlQuery,
  useRegionLoginQuery,
  useGetUserPermissionQuery,
  useLazyGetInstanceInfoQuery,
  useLazyGetInstanceListQuery,
  useLazyGetVmLiveStatusQuery
} from '@monorepo/shared';
import { debugMode, HomeElms, server } from '../config/const';
import {
  findRedirectNode,
  selectAccountOu,
  selectOU,
  updateSelected
} from '@monorepo/shared/store/fcOuSlice';
import {
  findRedirectAccount,
  selectAccounts,
  selectSystem,
  updateCurrentAccount
} from '@monorepo/shared/store/accountSlice';
import {
  selectDebugKey,
  selectDefaultRegion,
  selectSessionStatus,
  updateDebugKey
} from '@monorepo/shared/store/authSlice';
import { t } from 'i18next';
import { selectHasGroup } from '@monorepo/shared/store/groupSlice';
import { redirectVmUrl } from '@monorepo/shared/components/InstanceActions';

export const PrivateRoute = () => {
  let query = useQuery();
  const { pathname, state } = useLocation();
  const groupId = state?.group_id;
  const debugKey = useAppSelector(selectDebugKey);
  const accountHasGroup = useAppSelector(selectHasGroup);
  const defaultRegion = useAppSelector(selectDefaultRegion);
  const systemData = useAppSelector(selectSystem);
  const debug_key = query.get('debug_key') || state?.debug_key || debugKey;
  const redirect_data = query.get('redirect_data') || state?.redirect_data;
  const obj = parseRedirectAccountFromHkey(redirect_data);
  // Note: redirect_account, redirect_user, access_from, org_id is from VM redirect
  const redirect_account =
    query.get('redirect_account') || state?.redirect_account;
  const access_from = query.get('access_from') || state?.access_from;
  const org_id = query.get('org_id') || state?.org_id;

  const redirect_obj =
    state?.redirect_obj && state?.redirect_obj?.account_id
      ? state?.redirect_obj
      : obj?.account_id || redirect_account
        ? {
            account_id: obj?.account_id || redirect_account,
            user_id: obj?.user_id,
            node_id: obj?.node_id,
            org_id: org_id,
            access_from: {
              user_id: obj?.by_user_id || access_from,
              account_id: obj?.by_account_id
            }
          }
        : {};

  const user = query.get('user') || state?.user || getLocalRetentionUser();
  const dispatch = useAppDispatch();
  const sessionStatus = useAppSelector(selectSessionStatus);
  const ouData = useAppSelector(selectOU);
  const accounts = useAppSelector(selectAccounts);
  const accountOu = useAppSelector(selectAccountOu);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [regionLogin] = useLazyRegionLoginQuery();
  const [getInstanceList] = useLazyGetInstanceListQuery();
  const [getVmStatus] = useLazyGetVmLiveStatusQuery();
  const [getInstanceInfo] = useLazyGetInstanceInfoQuery();
  const [getInstanceUrl] = useLazyGetHaInstanceAccessUrlQuery();
  const [rAccount, setRAccount] = useState<Record<string, any>>({});

  const globalUser = useUserLoginQuery({
    key: debugMode ? debug_key : undefined,
    server: server
  });
  const ou = useGetUserOrgNodesQuery(
    { server: server, fcName: HomeElms?.fcName, refresh: true },
    {
      skip: !globalUser.isSuccess
    }
  );
  const productVersions = useGetProductVersionsQuery(
    { server: server },
    {
      skip: !globalUser.isSuccess
    }
  );

  const getRegions = useGetRegionsQuery(
    { server: server },
    {
      skip: !globalUser.isSuccess
    }
  );
  const { data: defaultRegionLoginData } = useRegionLoginQuery(
    {
      region: { ...defaultRegion, globalToken: globalUser.data?.region_token },
      enableTimezone: true
    },
    { skip: !globalUser.isSuccess || !defaultRegion?.url }
  );
  const permission = useGetUserPermissionQuery(
    {
      account_node_id: rAccount?.account_node_id,
      account_id: rAccount?.account_id,
      user_id: rAccount?.user_id,
      access_from_user_id: rAccount?.access_from?.user_id,
      access_from_account_id: rAccount?.access_from?.account_id,
      server: server
    },
    {
      skip: !rAccount?.account_id || !rAccount?.access_from || rAccount?.portals
    }
  );

  // save redirect info to local storage for debugging purposes
  const logObj = {
    redirect_data: redirect_data,
    redirect_account: redirect_account,
    access_from: access_from
  };
  updateLocalRedirectObj(logObj);
  useEffect(() => {
    if (debug_key) {
      dispatch(updateDebugKey(debug_key));
    }
  }, [debug_key]);

  const doRedirect = async (
    rAccount: Record<string, any>,
    region: Record<string, any>
  ) => {
    let qRegion = { ...region };

    try {
      if (!qRegion?.globalToken || qRegion?.token) {
        setIsLoading(false);
        return;
      }

      const regionObj = await regionLogin({
        region: qRegion,
        enableTimezone: true
      }).unwrap();

      qRegion = { ...qRegion, token: regionObj?.token };

      const instanceList = await getInstanceList({
        account: rAccount,
        region: qRegion
      }).unwrap();

      if (!instanceList?.length) {
        console.info('No instance found');
        setIsLoading(false);
        return;
      }

      const instance = instanceList[0];

      if (instance?.incomplete_products || instance?.incomplete) {
        console.info('Instance provisioning is incomplete');
        setIsLoading(false);
        return;
      }

      const [vmStatus, instanceInfo] = await Promise.all([
        getVmStatus({
          account: rAccount,
          region: qRegion,
          instance
        }).unwrap(),
        getInstanceInfo({
          account: rAccount,
          region: qRegion,
          instance
        }).unwrap()
      ]);

      if (!vmStatus?.is_active || instanceInfo?.state === 'UPGRADING') {
        console.info(
          'vm is not active or instance is upgrading',
          vmStatus,
          instanceInfo
        );
        setIsLoading(false);
        return;
      }

      const urlObj = await getInstanceUrl({
        account: rAccount,
        region: qRegion,
        instance
      }).unwrap();

      if (!urlObj?.url) {
        throw new Error('Instance URL is missing');
      }

      const success = redirectVmUrl(urlObj, '_self');
      if (!success) {
        throw new Error('Failed to redirect to VM URL');
      }

      return;
    } catch (error: any) {
      console.error('Redirect flow failed:', {
        message: error?.message,
        data: error?.data,
        status: error?.status
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!ou.isSuccess) {
      return;
    }

    const accountKeys = Object.keys(accounts);
    const hasSingleAccount = accountKeys.length === 1;
    const hasAccounts = accountKeys.length > 0;
    const hasRedirectAccount = !!redirect_obj?.account_id;
    const hasOuData = !!ouData?.length;
    const sourceData = hasOuData ? ouData : accountOu;

    let redirectObj = redirect_obj;

    // Auto select single account
    if (!groupId && !hasRedirectAccount && !hasOuData && hasSingleAccount) {
      const accountId = accountKeys[0];
      const account = accounts[accountId];

      redirectObj = {
        account_id: account?.account_id,
        user_id: account?.user_id
      };

      dispatch(updateCurrentAccount(account));
      setRAccount(account);

      if (pathname.includes('/cred/ou')) {
        navigate('/organization/account');
      }

      return;
    }

    // Existing redirect account flow
    if (!groupId && hasAccounts && hasRedirectAccount) {
      const checkAccount = findRedirectAccount({
        accounts,
        redirect_obj: redirectObj
      });

      const redirectNode = findRedirectNode(sourceData, redirectObj);

      if (redirectNode) {
        dispatch(updateSelected(copyObj(redirectNode)));
      }

      dispatch(updateCurrentAccount(checkAccount));
      setRAccount(checkAccount);

      return;
    }

    setIsLoading(false);
  }, [
    ou.isSuccess,
    accounts,
    redirect_obj,
    groupId,
    ouData,
    accountOu,
    pathname,
    navigate,
    dispatch
  ]);

  useEffect(() => {
    const shouldSkipLoadingReset = pathname.includes('/cred/ou');

    const stopLoading = () => {
      if (!shouldSkipLoadingReset) {
        setIsLoading(false);
      }
    };

    if (!rAccount?.account_id || !rAccount?.region || accountHasGroup) {
      stopLoading();
      return;
    }

    setIsLoading(true);

    const regionList = Array.isArray(rAccount.region)
      ? rAccount.region
      : [rAccount.region];

    const region = regionList.find((reg: Record<string, any>) => !!reg?.url);

    if (!region) {
      stopLoading();
      return;
    }

    const isAutoEnterVm = systemData?.auto_enter_vm ?? true;

    if (!isAutoEnterVm) {
      stopLoading();
      return;
    }

    doRedirect(rAccount, {
      ...region,
      globalToken: globalUser.data?.region_token
    });
  }, [
    rAccount,
    accountHasGroup,
    pathname,
    systemData?.auto_enter_vm,
    globalUser.data?.region_token
  ]);

  useEffect(() => {
    if (sessionStatus === 'REVOKED' || globalUser?.isError) {
      window.open(getSafeUrl(`${server}`), '_self');
    }
  }, [sessionStatus, globalUser?.isError]);

  return (
    <>
      {isLoading ? (
        <Apploader
          labelText={
            globalUser?.isError
              ? 'Global User Login Error!'
              : t('common.loading') || 'loading...'
          }
          showIcon
        />
      ) : (
        <Outlet
          context={{
            redirect_obj: redirect_obj
          }}
        />
      )}
    </>
  );
};
